import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { ActionDto, HttpActionDto } from '@/Domain/api/Action/dto/Action.dto';

export type UpdateActionForm = Partial<{
  name: string;
  description: string;
  icon: string;
  icon_color: string;
  type: string;
  is_active: boolean;
}>;

export const useUpdateActionDetails = () => {
  const { t } = useTranslation('actions_page');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: [QueryKey.actions.basic.patch],
    mutationFn: ({
      actionId,
      data,
    }: {
      actionId: number;
      showToast: boolean;
      data: UpdateActionForm;
    }) => ActionsApi.update(actionId, data),
    onSuccess: (updatedAction, { actionId, showToast }) => {
      if (showToast && updatedAction.is_active) {
        toast.success(t('tooltip.action_enabled'));
      }
      if (showToast && !updatedAction.is_active) {
        toast.info(t('tooltip.action_disabled'));
      }
      void queryClient.invalidateQueries({ queryKey: ['subscription'] });
      void queryClient.setQueryData(
        [QueryKey.actions.httpRequest.get, actionId],
        (prev: HttpActionDto) => ({ ...prev, ...updatedAction }),
      );
      void queryClient.setQueryData<ActionDto[]>([QueryKey.actions.basic.list], (old) => {
        if (!old) return [updatedAction];
        return old.map((item) => (item.id === updatedAction.id ? updatedAction : item));
      });
    },
  });

  const handleUpdateDetails = (actionId: number, data: UpdateActionForm, showToast = false) =>
    mutation.mutateAsync({ data, showToast, actionId });

  return { handleUpdateDetails, isActionPending: mutation.isPending };
};
